// Deterministic fake-identity generator for Demo Mode's "scramble names" toggle.
// Same real value always maps to the same fake value for the life of a demo
// session (via the shared nameMap), so a person stays recognizable across screens.

const FIRST_NAMES = [
  'Alex', 'Jordan', 'Taylor', 'Morgan', 'Casey', 'Riley', 'Avery', 'Quinn', 'Reese', 'Rowan',
  'Skyler', 'Emerson', 'Hayden', 'Dakota', 'Sawyer', 'Peyton', 'Charlie', 'Drew', 'Jamie', 'Cameron',
  'Blake', 'Sam', 'Micah', 'Elliot', 'Finley', 'Harper', 'Kendall', 'Logan', 'Parker', 'River',
]

const LAST_NAMES = [
  'Bennett', 'Hayes', 'Sullivan', 'Reed', 'Coleman', 'Foster', 'Nash', 'Whitfield', 'Mercer', 'Blake',
  'Donovan', 'Ellison', 'Hartley', 'Marsh', 'Pratt', 'Quincy', 'Sinclair', 'Tanner', 'Voss', 'Wren',
  'Ashford', 'Briggs', 'Calloway', 'Dorsey', 'Ferris', 'Grady', 'Holt', 'Ives', 'Jennings', 'Kerr',
]

// Confirmed name/email/phone field names from backend/app/database/models.py
// (Contact, Teacher) and the lessons attendance JSON blob; `student` is the
// Scheduling tab's check-in cards (utils/excused_streaks.py's as_json).
const NAME_KEYS = new Set(['name', 'teacher_name', 'student_name', 'contact_name', 'student'])
const EMAIL_KEYS = new Set(['email'])
const PHONE_KEYS = new Set(['phone'])
const TITLE_KEYS = new Set(['lesson_title'])

function hashString(str) {
  let h = 0
  for (let i = 0; i < str.length; i++) {
    h = (h * 31 + str.charCodeAt(i)) | 0
  }
  return Math.abs(h)
}

function fakeNameFor(seed) {
  const h = hashString(seed)
  const first = FIRST_NAMES[h % FIRST_NAMES.length]
  const last = LAST_NAMES[(h >> 3) % LAST_NAMES.length]
  return `${first} ${last}`
}

function fakeEmailFor(fakeName) {
  const [first, last] = fakeName.toLowerCase().split(' ')
  return `${first}.${last}@example.com`
}

function fakePhoneFor(seed) {
  const h = hashString(seed)
  return `(555) 010-${String(1000 + (h % 9000))}`
}

function scrambleValue(kind, realValue, nameMap, contextFakeName) {
  const cacheKey = `${kind}:${realValue}`
  if (nameMap.has(cacheKey)) return nameMap.get(cacheKey)

  let fake
  if (kind === 'email') {
    fake = fakeEmailFor(contextFakeName || fakeNameFor(realValue))
  } else if (kind === 'phone') {
    fake = fakePhoneFor(contextFakeName || realValue)
  } else {
    fake = fakeNameFor(realValue)
  }
  nameMap.set(cacheKey, fake)
  return fake
}

// Public entry point for scrambling a standalone name outside a JSON walk
// (e.g. the "previewing as" label in the header) — same cache, so it always
// agrees with whatever scrambleJson produced for that same person elsewhere.
export function scrambleName(realValue, nameMap) {
  return scrambleValue('name', realValue, nameMap)
}

function collectNamePairs(data, nameMap, pairs) {
  if (Array.isArray(data)) {
    data.forEach(item => collectNamePairs(item, nameMap, pairs))
    return
  }
  if (data && typeof data === 'object') {
    for (const [key, value] of Object.entries(data)) {
      if (NAME_KEYS.has(key) && typeof value === 'string' && value) {
        pairs.push([value, scrambleValue('name', value, nameMap)])
      } else {
        collectNamePairs(value, nameMap, pairs)
      }
    }
  }
}

// Lesson titles are free text that often embeds a student's name verbatim
// (e.g. "Jane Smith - Guitar"). Substitute every known real name out of the
// text, longest first, so a short name doesn't clobber part of a longer one.
function applyTitleSubstitution(title, pairs) {
  let result = title
  for (const [real, fake] of [...pairs].sort((a, b) => b[0].length - a[0].length)) {
    if (real && result.includes(real)) result = result.split(real).join(fake)
  }
  return result
}

// Lesson titles are raw text scraped from MSM with no guaranteed structure,
// and the same real person can be spelled differently across records (known
// data-quality issue — e.g. Contact "Nate Glenney" vs Student "Nate Glenny"
// on the very same lesson). Exact-match substitution alone misses that, so
// after trying it, always force-scramble the leading "name" segment (before
// the first " - ", or the whole string if there's no dash) on its own text
// too — that way a spelling mismatch never leaves a real name untouched.
function scrambleTitle(title, pairs, nameMap) {
  const substituted = applyTitleSubstitution(title, pairs)
  const dashIndex = substituted.indexOf(' - ')
  const leading = dashIndex === -1 ? substituted : substituted.slice(0, dashIndex)
  const rest = dashIndex === -1 ? '' : substituted.slice(dashIndex)
  const alreadyFake = pairs.some(([, fake]) => leading === fake || leading.includes(fake))
  if (!leading || alreadyFake) return substituted
  return scrambleValue('name', leading, nameMap) + rest
}

// Recursively rewrites name/email/phone fields in a parsed JSON response body.
// When an object has both a name-ish field and an email/phone field, the
// email/phone is derived from that same fake identity so they stay consistent.
export function scrambleJson(data, nameMap) {
  if (Array.isArray(data)) {
    return data.map(item => scrambleJson(item, nameMap))
  }
  if (data && typeof data === 'object') {
    let contextFakeName = null
    for (const key of Object.keys(data)) {
      if (NAME_KEYS.has(key) && typeof data[key] === 'string' && data[key]) {
        contextFakeName = scrambleValue('name', data[key], nameMap)
        break
      }
    }

    // Only bother scanning for embedded names if this object actually has a
    // title field worth rewriting.
    let titlePairs = null
    for (const key of Object.keys(data)) {
      if (TITLE_KEYS.has(key) && typeof data[key] === 'string' && data[key]) {
        titlePairs = []
        collectNamePairs(data, nameMap, titlePairs)
        break
      }
    }

    const result = {}
    for (const [key, value] of Object.entries(data)) {
      if (TITLE_KEYS.has(key) && typeof value === 'string' && value && titlePairs) {
        result[key] = scrambleTitle(value, titlePairs, nameMap)
      } else if (NAME_KEYS.has(key) && typeof value === 'string' && value) {
        result[key] = scrambleValue('name', value, nameMap)
      } else if (EMAIL_KEYS.has(key) && typeof value === 'string' && value) {
        result[key] = scrambleValue('email', value, nameMap, contextFakeName)
      } else if (PHONE_KEYS.has(key) && typeof value === 'string' && value) {
        result[key] = scrambleValue('phone', value, nameMap, contextFakeName)
      } else {
        result[key] = scrambleJson(value, nameMap)
      }
    }
    return result
  }
  return data
}
